import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner, faArrowRight, faClock, faUser, faPhone, faBan, faBookOpen, faPenToSquare, faXmark, faDoorOpen} from '@fortawesome/free-solid-svg-icons';
import {FcGoogle} from 'react-icons/fc';
import {toast} from 'react-hot-toast';
import {GlobalToaster} from '../components/UI/GlobalToaster';
import {useDocumentTitle} from "../hooks/useDocumentTitle";
import {TechnicalSupportButton} from "../components/UI/TechnicalSupportButton";
import {RoomPicker} from "../components/UI/RoomPicker";
import {useAuth} from "../contexts/AuthContext";
import {useConfirm} from '../contexts/ConfirmContext';
import {ref, onValue, set, remove} from 'firebase/database';
import {db} from '../utils/firebaseConfig';
import {sanitizeEmail} from '../utils/roles';
import {SUPPORT_ZALO_PHONE} from '../utils/constants';

export const HomePage: React.FC = () => {
    useDocumentTitle('Nhà 48');
    const {isAuthenticated, loading, user, role, signInWithGoogle, logout} = useAuth();
    const confirm = useConfirm();
    const navigate = useNavigate();

    const [rooms, setRooms] = useState<{ id: string, name: string, description?: string }[]>([]);
    const [registrationStatus, setRegistrationStatus] = useState<'none' | 'pending' | 'rejected'>('none');
    const [registration, setRegistration] = useState<Record<string, any> | null>(null);
    const [isCheckingData, setIsCheckingData] = useState(false);
    // Đang chờ duyệt nhưng người thuê bấm "Chọn phòng khác" -> mở lại form đè lên
    // màn hình chờ. Không xoá đăng ký cũ lúc này: bấm nhầm rồi quay lại thì yêu
    // cầu vẫn còn nguyên, chủ nhà vẫn duyệt được.
    const [isEditing, setIsEditing] = useState(false);

    // Form state
    const [regName, setRegName] = useState('');
    const [regRoomId, setRegRoomId] = useState('');
    const [regPhone, setRegPhone] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Set default name from Google Profile
    useEffect(() => {
        if (user?.displayName && !regName) {
            setRegName(user.displayName);
        }
    }, [user]);

    // Chuyển hướng khi đã đăng nhập và có vai trò hợp lệ.
    // Chủ nhà/quản lý vào thẳng trang Ghi điện nước - đây là trang dùng nhiều
    // nhất hằng tháng, không phải Quản lý nhà trọ.
    useEffect(() => {
        if (!loading && isAuthenticated) {
            if (role === 'tenant') {
                navigate('/phong-cua-toi');
            } else {
                navigate('/diennuoc');
            }
        }
    }, [isAuthenticated, loading, role, navigate]);

    // Danh sách phòng để đổ vào ô chọn của form đăng ký (sau khi đã đăng nhập
    // nhưng chưa có quyền). Đọc CÔNG KHAI, không chờ đăng nhập xong mới tải -
    // để lúc user gõ xong Google, ô chọn phòng có sẵn dữ liệu ngay, không phải
    // đợi thêm một vòng mạng. Trang đăng nhập KHÔNG hiện danh sách này ra
    // ngoài; nó chỉ xuất hiện dưới dạng dropdown trong form đăng ký.
    useEffect(() => {
        const roomsRef = ref(db, 'roomDirectory');
        const unsubRooms = onValue(roomsRef, (snap) => {
            if (snap.exists()) {
                const data = snap.val();
                const roomList = Object.keys(data).map(key => ({
                    id: key,
                    name: data[key].name,
                    description: data[key].description || '',
                }));
                // Sort phòng theo tên (số)
                roomList.sort((a, b) => a.name.localeCompare(b.name, undefined, {numeric: true}));
                setRooms(roomList);
                // KHÔNG chọn sẵn phòng đầu danh sách: người thuê lướt nhanh qua
                // form sẽ gửi nhầm yêu cầu vào phòng 1, chủ nhà duyệt xong mới
                // phát hiện. Để trống thì họ buộc phải tự chọn đúng phòng mình.
            } else {
                setRooms([]);
            }
        });
        return () => unsubRooms();
    }, []);

    // Fetch trạng thái đăng ký khi user đăng nhập nhưng chưa có quyền
    useEffect(() => {
        if (!loading && user && !role) {
            setIsCheckingData(true);
            const email = sanitizeEmail(user.email!);

            const regRef = ref(db, `tenantRegistrations/${email}`);
            const unsubReg = onValue(regRef, (snap) => {
                if (snap.exists()) {
                    setRegistration(snap.val());
                    setRegistrationStatus(snap.val().status || 'pending');
                } else {
                    setRegistration(null);
                    setRegistrationStatus('none');
                }
                setIsCheckingData(false);
            });

            return () => unsubReg();
        }
    }, [loading, user, role]);

    const handleSubmitRegistration = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!regName.trim()) {
            toast.error('Vui lòng điền họ tên');
            return;
        }
        // Ô chọn phòng không còn chọn sẵn phòng nào, nên nhắc đúng việc còn thiếu
        // thay vì câu "điền đủ thông tin" chung chung.
        if (!regRoomId) {
            toast.error('Vui lòng chọn phòng bạn đang thuê');
            return;
        }

        setIsSubmitting(true);
        try {
            const emailKey = sanitizeEmail(user!.email!);
            const payload: Record<string, any> = {
                email: user!.email,
                name: regName.trim(),
                roomId: regRoomId,
                status: 'pending',
                createdAt: Date.now(),
            };
            // SĐT không bắt buộc - chỉ ghi khi người dùng có điền
            if (regPhone.trim()) {
                payload.phone = regPhone.trim();
            }
            await set(ref(db, `tenantRegistrations/${emailKey}`), payload);
            setIsEditing(false);
            toast.success(
                isEditing ? 'Đã đổi yêu cầu!'
                    : registrationStatus === 'rejected' ? 'Đã gửi lại yêu cầu!'
                        : 'Gửi yêu cầu thành công!',
            );
        } catch (error: any) {
            console.error('Error submitting registration:', error);
            // Dán mã lỗi của Firebase vào toast. Đây là màn hình của người lạ
            // chưa có quyền gì: họ không mở được console, không nhắn được cho ai
            // ngoài chủ nhà, mà "Không thể gửi yêu cầu" thì không nói được gì -
            // PERMISSION_DENIED (rules) hay mất mạng cũng ra đúng một câu.
            const code = String(error?.code || error?.message || '').split(':')[0].trim();
            toast.error(code ? `Không gửi được yêu cầu (${code})` : 'Không thể gửi yêu cầu');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Mở lại form với đúng những gì đã gửi, để người thuê chỉ phải sửa ô sai
    // (thường là ô phòng) chứ không gõ lại từ đầu.
    const handleEditRegistration = () => {
        setRegName(registration?.name || user?.displayName || '');
        setRegRoomId(registration?.roomId || '');
        setRegPhone(registration?.phone || '');
        setIsEditing(true);
    };

    // Huỷ hẳn: dùng khi họ không định đăng ký nữa (chứ đổi phòng thì sửa yêu cầu
    // là đủ). Rules chỉ cho xoá khi còn 'pending' - đã duyệt rồi thì đây là việc
    // của chủ nhà.
    const handleCancelRegistration = async () => {
        const roomLabel = rooms.find(r => r.id === registration?.roomId)?.name;
        if (!await confirm({
            title: 'Huỷ yêu cầu nhận phòng?',
            subtitle: roomLabel ? `Phòng ${roomLabel}` : undefined,
            description: 'Bạn có thể đăng ký lại phòng khác ngay sau đó.',
            tone: 'danger',
            confirmLabel: 'Huỷ yêu cầu',
        })) return;

        setIsSubmitting(true);
        try {
            await remove(ref(db, `tenantRegistrations/${sanitizeEmail(user!.email!)}`));
            setIsEditing(false);
            setRegRoomId('');
            setRegPhone('');
            toast.success('Đã huỷ yêu cầu');
        } catch (error) {
            console.error(error);
            toast.error('Không huỷ được yêu cầu');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Trong lúc kiểm tra đăng nhập: hiện splash để tránh nháy form
    if (loading || (isAuthenticated && !isCheckingData)) {
        return (
            <div className="min-h-app flex flex-col items-center justify-center lg-app-bg">
                <div className="inline-flex items-center justify-center w-24 h-24 glass-card mb-6 animate-fade-in">
                    <img src="/images/logo.svg" alt="Nhà 48" className="w-16 h-16 object-contain"/>
                </div>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-slate-500 dark:text-slate-400"/>
                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Đang tải…</p>
            </div>
        );
    }

    return (
        <div className="min-h-app flex flex-col">
            <div className="relative overflow-hidden flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 lg-app-bg">
                {/* Blob nền chuyển động nhẹ - cùng bộ hue với mesh Aurora trong
                    .lg-app-bg (indigo / fuchsia / cyan). Đây là trang đăng nhập,
                    màn hình đầu tiên người ta thấy, nên blob để đậm hơn phần còn
                    lại của app một chút. */}
                <div aria-hidden="true" className="pointer-events-none absolute inset-0">
                    <div className="lg-blob absolute -top-24 -left-16 w-72 h-72 rounded-full bg-indigo-500/30 dark:bg-indigo-500/25 blur-3xl"/>
                    <div className="lg-blob absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-fuchsia-500/25 dark:bg-fuchsia-500/20 blur-3xl" style={{animationDelay: '-8s'}}/>
                    <div className="lg-blob absolute -bottom-28 left-1/4 w-72 h-72 rounded-full bg-cyan-400/25 dark:bg-cyan-400/20 blur-3xl" style={{animationDelay: '-16s'}}/>
                </div>
                
                <div className="relative z-10 max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="mx-auto mb-5 inline-flex items-center justify-center w-24 h-24 glass-card">
                            <img src="/images/logo.svg" alt="Nhà 48 Logo" className="w-16 h-16 object-contain" />
                        </div>
                        <div className="space-y-2">
                            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                                Nhà 48
                            </h1>
                            <h2 className="text-base md:text-lg text-slate-600 dark:text-slate-300 font-medium">
                                Quản lý điện nước & thanh toán
                            </h2>
                        </div>
                    </div>

                    {!user ? (
                        <div className="glass-card p-6 sm:p-8 animate-fade-in-up">
                            <div className="mb-5 text-center">
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                                    Đăng nhập hệ thống
                                </h3>
                                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                    Đăng nhập bằng tài khoản Google để tiếp tục
                                </p>
                            </div>

                            <button
                                onClick={signInWithGoogle}
                                className="w-full flex justify-center items-center gap-3 px-4 py-3
                                         rounded-lg text-white font-medium bg-slate-800
                                         hover:bg-slate-900 shadow-sm
                                         focus:outline-none transition-all active:scale-[0.99]"
                            >
                                {/* @ts-ignore */}
                                <FcGoogle className="text-xl bg-white rounded-full" />
                                Đăng nhập bằng Google
                            </button>

                            {/* Người mới cần biết đúng MỘT điều ở màn hình này: đăng nhập
                                xong chưa vào được ngay, còn phải chờ chủ nhà duyệt. Các
                                bước còn lại để /huong-dan kể kèm hình - trước đây chép cả
                                ba bước ra đây thì một màn hình có hai bản hướng dẫn gần y
                                hệt nhau, lại thêm hai đường dẫn cùng trỏ một chỗ. */}
                            <p className="mt-5 pt-5 border-t border-white/40 dark:border-white/10
                                text-xs text-slate-500 dark:text-slate-400 text-center leading-relaxed">
                                Nếu là lần đầu sử dụng thì Đăng nhập xong bạn chọn phòng đang thuê và gửi yêu cầu,
                                chủ nhà duyệt là vào được phòng của mình.
                            </p>
                        </div>
                    ) : (
                        <div className="glass-card p-6 sm:p-8 animate-fade-in-up">
                            <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/20 dark:border-white/10">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-slate-800 dark:text-white truncate">
                                        {user.email}
                                    </p>
                                    <p className="text-xs text-slate-500">Chưa được cấp quyền</p>
                                </div>
                                <button onClick={logout} className="glass-btn px-3 py-1.5 rounded-lg text-xs font-medium">
                                    Đổi tài khoản
                                </button>
                            </div>

                            {isCheckingData ? (
                                <div className="py-8 text-center">
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin text-slate-500 text-xl" />
                                </div>
                            ) : registrationStatus === 'pending' && !isEditing ? (
                                <div className="py-4 text-center">
                                    <div className="w-16 h-16 rounded-full bg-amber-400/20 text-amber-500 flex items-center justify-center mx-auto mb-4">
                                        <FontAwesomeIcon icon={faClock} className="text-2xl" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                                        Đang chờ chủ nhà duyệt
                                    </h3>

                                    {/* Nhắc lại phòng đã chọn ngay ở đây: chọn nhầm phòng là lỗi
                                        hay gặp nhất, mà trước đây màn hình chờ không hiện phòng
                                        nào cả nên chẳng ai biết mình bấm nhầm. */}
                                    {registration?.roomId && (
                                        <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-3 rounded-full
                                            bg-white/50 dark:bg-white/10 text-sm font-medium text-slate-700 dark:text-slate-200">
                                            <FontAwesomeIcon icon={faDoorOpen} className="text-slate-500" />
                                            Phòng {rooms.find(r => r.id === registration.roomId)?.name || registration.roomId}
                                        </div>
                                    )}

                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                        Yêu cầu nhận phòng của bạn đã được gửi. Chủ nhà duyệt xong bạn sẽ vào được
                                        trang phòng của mình - có thể đóng trang này và quay lại sau.
                                    </p>

                                    <div className="mt-5 pt-4 border-t border-white/40 dark:border-white/10 flex flex-col gap-2">
                                        <button
                                            type="button"
                                            onClick={handleEditRegistration}
                                            disabled={isSubmitting}
                                            className="glass-btn w-full flex justify-center items-center gap-2 px-4 py-2.5
                                                     rounded-lg text-sm font-medium disabled:opacity-50"
                                        >
                                            <FontAwesomeIcon icon={faPenToSquare} />
                                            Chọn phòng khác / sửa thông tin
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCancelRegistration}
                                            disabled={isSubmitting}
                                            className="w-full flex justify-center items-center gap-2 px-4 py-2
                                                     rounded-lg text-sm font-medium text-rose-600 dark:text-rose-400
                                                     hover:bg-rose-500/10 transition-colors disabled:opacity-50"
                                        >
                                            <FontAwesomeIcon icon={isSubmitting ? faSpinner : faXmark} className={isSubmitting ? 'animate-spin' : ''} />
                                            Huỷ yêu cầu
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmitRegistration} className="space-y-4">
                                    {isEditing ? (
                                        <div className="text-center mb-6">
                                            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                                                Sửa yêu cầu nhận phòng
                                            </h3>
                                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                                Yêu cầu cũ sẽ được thay bằng thông tin mới
                                            </p>
                                        </div>
                                    ) : registrationStatus === 'rejected' ? (
                                        <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 mb-2">
                                            <FontAwesomeIcon icon={faBan} className="text-rose-500 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-semibold text-rose-600 dark:text-rose-400">
                                                    Yêu cầu trước đã bị từ chối
                                                </p>
                                                <p className="text-xs text-rose-500/80 dark:text-rose-400/70 mt-0.5">
                                                    Kiểm tra lại thông tin (đặc biệt là phòng) rồi gửi lại yêu cầu.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center mb-6">
                                            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                                                Đăng ký nhận phòng
                                            </h3>
                                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                                Điền thông tin rồi gửi - chủ nhà duyệt xong bạn sẽ vào được phòng
                                            </p>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                                            Họ và tên
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                <FontAwesomeIcon icon={faUser} className="text-slate-500" />
                                            </div>
                                            <input
                                                type="text"
                                                required
                                                value={regName}
                                                onChange={(e) => setRegName(e.target.value)}
                                                className="glass-input block w-full pl-11 pr-3 py-2.5 text-sm"
                                                placeholder="Nguyễn Văn A"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                                            Chọn phòng
                                        </label>
                                        <RoomPicker
                                            rooms={rooms}
                                            value={regRoomId}
                                            onChange={setRegRoomId}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                                            Số điện thoại <span className="text-slate-500 font-normal">(không bắt buộc)</span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                <FontAwesomeIcon icon={faPhone} className="text-slate-500" />
                                            </div>
                                            <input
                                                type="tel"
                                                value={regPhone}
                                                onChange={(e) => setRegPhone(e.target.value)}
                                                className="glass-input block w-full pl-11 pr-3 py-2.5 text-sm"
                                                placeholder="09xxxxxxxx"
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full flex justify-center items-center gap-2 px-4 py-2.5 mt-2
                                                 rounded-lg text-white font-medium bg-slate-800
                                                 hover:bg-slate-900 shadow-sm
                                                 transition-all disabled:opacity-50"
                                    >
                                        {isSubmitting ? (
                                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faArrowRight} />
                                                {isEditing ? 'Cập nhật yêu cầu'
                                                    : registrationStatus === 'rejected' ? 'Gửi lại yêu cầu'
                                                        : 'Gửi yêu cầu'}
                                            </>
                                        )}
                                    </button>

                                    {isEditing && (
                                        <button
                                            type="button"
                                            onClick={() => setIsEditing(false)}
                                            className="w-full px-4 py-2 rounded-lg text-sm font-medium
                                                     text-slate-500 dark:text-slate-400 hover:bg-white/40
                                                     dark:hover:bg-white/10 transition-colors"
                                        >
                                            Quay lại
                                        </button>
                                    )}
                                </form>
                            )}
                        </div>
                    )}

                    <div className="text-center text-sm text-slate-500 dark:text-slate-400 space-y-1.5">
                        <button
                            type="button"
                            onClick={() => navigate('/huong-dan')}
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/10 transition-colors"
                        >
                            <FontAwesomeIcon icon={faBookOpen} />
                            Hướng dẫn dùng app cho người thuê
                        </button>
                        <p>Số 48, đường số 6 · Chủ nhà: Thủy Hồng</p>
                    </div>
                </div>
            </div>

            <TechnicalSupportButton phoneNumber={SUPPORT_ZALO_PHONE}/>

            <footer className="py-4 text-center text-sm text-slate-500 dark:text-slate-400
                          bg-white/30 dark:bg-white/5 sm:backdrop-blur border-t border-white/40 dark:border-white/10">
                © 2025 Bùi Tiến Tuân. All rights reserved.
            </footer>
            
            <GlobalToaster />
        </div>
    );
};